/**
 * Agent-set (teams of agents) API client.
 *
 * Mirrors the conventions in `./agent.js`: a thin wrapper over the shared
 * authenticated axios instance from `../lib/client`. An agent set is created,
 * read, updated and deleted as a single document whose `agents` members
 * reference each other with `label:<label>` shortforms (resolved server-side).
 * See the backend `multi-agent-api` docs for the document shape.
 */

import { api } from '../lib/client';

/**
 * List the current user's agent sets (summary rows).
 * @returns {Promise<{agentSets: Array<{id,name,description,createdAt,updatedAt,agents:Array}>}>}
 *   each member summarised as `{id,label,name,modelName,type}`.
 */
export async function listAgentSets() {
  const { data } = await api.get('/agent-sets');
  return data;
}

/**
 * Fetch one agent set with its complete member agent definitions
 * (prompt, functions, mcpServers, …). Member `keys` are never returned.
 * @param {string} id agent-set id
 */
export async function getAgentSet(id) {
  const { data } = await api.get(`/agent-sets/${id}`);
  return data;
}

/**
 * Create a set and all member agents from one document.
 * @param {{name?:string, description?:string, agents:Array}} doc set document
 * @returns the created set with member `label:` references resolved to ids.
 */
export async function createAgentSet({ name, description, agents }) {
  const { data } = await api.post('/agent-sets', { name, description, agents });
  return data;
}

/**
 * Promote a legacy set-less agent into a new single-member set. The agent is
 * MOVED into the set (it keeps its id, deployments and call history) — not
 * duplicated. The agent must not already belong to a set.
 * @param {string} agentId existing set-less agent id
 * @param {{name?:string}} [opts] optional name for the new set (defaults to the agent name)
 * @returns the created set (member `label:` references resolved to ids)
 */
export async function promoteAgentToSet(agentId, { name } = {}) {
  const body = { agentId };
  if (name != null) body.name = name;
  const { data } = await api.post('/agent-sets/from-agent', body);
  return data;
}

/**
 * Update a set as a group: members are reconciled by `label`
 * (existing → updated in place, new → created, missing → deleted).
 * @param {string} id agent-set id
 * @param {{name?:string, description?:string, agents:Array}} doc set document
 */
export async function updateAgentSet(id, { name, description, agents }) {
  const body = { agents };
  if (name != null) body.name = name;
  if (description != null) body.description = description;
  const { data } = await api.put(`/agent-sets/${id}`, body);
  return data;
}

/**
 * Delete a set and every member agent. Active listeners on members must be
 * stopped first (as for any agent delete).
 * @param {string} id agent-set id
 */
export async function deleteAgentSet(id) {
  const { data } = await api.delete(`/agent-sets/${id}`);
  return data;
}
